import { debounce } from "@/lib/utils";
import { RhIcon, RhInput, RhSelect } from "@rhythm-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { FormikErrors } from "formik";
import React, { useMemo, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";

import { JobFormType } from "@/types/job";
import { SkillType } from "@/types/utils";

import api from "@/services/api";

import { Button } from "./ui/Button";
import { Chip } from "./utils/Chip";

const JobSkillPrioritization = ({
  values,
  setFieldValue,
}: {
  values: JobFormType;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean,
  ) => Promise<void | FormikErrors<JobFormType>>;
}) => {
  return (
    <div className="space-y-tsm">
      {values.priority_skills__form_value.length === 0 ? (
        <div className="background-gray px-tsm py-tlg">
          <p>
            Add skills above to prioritize based on employer’s importance and
            get the weightage
          </p>
        </div>
      ) : (
        <div className="background-gray space-y-tsm px-tmini py-tsm">
          <div className="space-y-tpico">
            <h4>Skill Prioritization</h4>
            <p className="text-hint">
              You can group multiple skills in the containers provided below
            </p>
          </div>

          <DragDropContext
            onDragEnd={(result) => {
              // console.log(result, "sd");
              if (!result.destination) return;
              const { source, destination } = result;
              const newRows = [...values.priority_skills__form_value];

              const [reorderedItem] = newRows[source.droppableId].splice(
                source.index,
                1,
              );
              newRows[destination.droppableId].splice(
                destination.index,
                0,
                reorderedItem,
              );

              // if at this point, there is a skill on the last row, add one more row after that
              if (
                newRows.length >= 2 &&
                newRows[newRows.length - 2].length > 0
              ) {
                newRows.splice(newRows.length - 1, 0, []);
              }

              setFieldValue("priority_skills__form_value", newRows);
            }}
          >
            {values.priority_skills__form_value.length > 1 &&
              values.priority_skills__form_value
                .slice(0, -1)
                .map((skill_group, rowIndex) => (
                  <Droppable
                    key={rowIndex}
                    droppableId={rowIndex.toString()}
                    direction="horizontal"
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className="flex min-h-[48px] gap-tpico overflow-scroll rounded border border-dashed px-tmini py-tmini"
                      >
                        {skill_group.map((skillInfo, cellIndex) => (
                          <Draggable
                            key={skillInfo.skill.id}
                            draggableId={skillInfo.skill.id.toString()}
                            index={cellIndex}
                          >
                            {(provided) => (
                              <Chip
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                size="sm"
                                className="whitespace-nowrap"
                              >
                                {skillInfo?.skill?.name}{" "}
                                {skillInfo.min_exp && `(${skillInfo.min_exp}y)`}
                                {"  "}
                                <button
                                  className="ml-2 rounded-full hover:bg-gray-400"
                                  onClick={() => {
                                    const updatedSkills =
                                      values.priority_skills__form_value.map(
                                        (group) =>
                                          group.filter(
                                            (skill) =>
                                              skill.skill.id !==
                                              skillInfo.skill.id,
                                          ),
                                      );

                                    if (
                                      updatedSkills.length >= 3 &&
                                      updatedSkills[updatedSkills.length - 2]
                                        .length === 0 &&
                                      updatedSkills[updatedSkills.length - 3]
                                        .length === 0
                                    ) {
                                      updatedSkills.splice(
                                        updatedSkills.length - 2,
                                        1,
                                      );
                                    }

                                    setFieldValue(
                                      "priority_skills__form_value",
                                      updatedSkills,
                                    );
                                  }}
                                >
                                  <RhIcon icon="tabler:x" className="h-4 w-4" />
                                </button>
                              </Chip>
                            )}
                          </Draggable>
                        ))}
                      </div>
                    )}
                  </Droppable>
                ))}

            <div className="mt-tsm space-y-tmini">
              <div className="space-y-tpico">
                <h4>Non Deal Breaker skills</h4>
                <p className="text-hint">
                  {`Place & rank the skills above according to the employer's
                  priorities and their importance to this job`}
                </p>
              </div>

              <Droppable
                droppableId={(
                  values.priority_skills__form_value.length - 1
                ).toString()}
                direction="horizontal"
              >
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="flex min-h-[48px] gap-tpico overflow-scroll rounded border border-dashed px-tmini py-tmini"
                  >
                    {values.priority_skills__form_value[
                      values.priority_skills__form_value.length - 1
                    ].map((skillInfo, cellIndex) => (
                      <Draggable
                        key={skillInfo.skill.id}
                        draggableId={skillInfo.skill.id.toString()}
                        index={cellIndex}
                      >
                        {(provided) => (
                          <Chip
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            size="sm"
                            className="whitespace-nowrap"
                          >
                            {skillInfo?.skill?.name}{" "}
                            {skillInfo.min_exp && `(${skillInfo.min_exp}y)`}
                            {"  "}
                            <button
                              className="ml-2 rounded-full hover:bg-gray-400"
                              onClick={() => {
                                const updatedSkills =
                                  values.priority_skills__form_value.map(
                                    (group) =>
                                      group.filter(
                                        (skill) =>
                                          skill.skill.id !== skillInfo.skill.id,
                                      ),
                                  );

                                setFieldValue(
                                  "priority_skills__form_value",
                                  updatedSkills,
                                );
                              }}
                            >
                              <RhIcon icon="tabler:x" className="h-4 w-4" />
                            </button>
                          </Chip>
                        )}
                      </Draggable>
                    ))}
                  </div>
                )}
              </Droppable>
            </div>
          </DragDropContext>
        </div>
      )}

      <AddSkill setFieldValue={setFieldValue} values={values} />
    </div>
  );
};

export default JobSkillPrioritization;

const AddSkill = ({
  setFieldValue,
  values,
}: {
  values: JobFormType;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean,
  ) => Promise<void | FormikErrors<JobFormType>>;
}) => {
  const [skillInfo, setSkillInfo] = useState({
    skill: undefined,
    min_exp: undefined,
  } as { skill: SkillType | undefined; min_exp: number | undefined });

  const skillsToBeExcluded = useMemo(() => {
    const allSkills = values.priority_skills__form_value
      .flat()
      .map((skillInfo) => skillInfo.skill);
    return allSkills.filter((skill) => skill !== undefined);
  }, [values.priority_skills__form_value]);

  const queryClient = useQueryClient();

  const loadOptions = debounce((input: string, callback) => {
    return queryClient
      .fetchQuery(["SKILLS", "LIST", { search: input }], () =>
        api.candidate.getSkills({ search: input }),
      )
      .then((data) =>
        callback(
          data.data.results.filter((skill: SkillType) => {
            return (
              skillsToBeExcluded.findIndex(
                (selectedSkill: SkillType) => selectedSkill.id === skill.id,
              ) === -1
            );
          }),
        ),
      );
  }, 700);

  return (
    <div className="flex items-center gap-tm2">
      <div>
        <p className="text-hint required-asterisk">Skill</p>

        <RhSelect
          isClearable={false}
          key={JSON.stringify(skillInfo)}
          value={skillInfo.skill}
          placeholder={"Select"}
          backspaceRemovesValue={false}
          onChange={(value) =>
            setSkillInfo((state) => ({ ...state, skill: value }))
          }
          async
          loadOptions={loadOptions}
          cacheOptions
          defaultOptions={true}
          menuPortalTarget={
            typeof window !== "undefined" &&
            document.querySelector("#theme-provider")
          }
          menuPlacement="auto"
          components={{
            IndicatorSeparator: () => null,
          }}
          getOptionLabel={(data: any) => data?.name}
          getOptionValue={(data: any) => data?.id}
          className="min-w-[250px]"
        />
      </div>

      <span>-</span>

      <div>
        <p className="text-hint">
          Min Required Experience <span className="body-small">(y)</span>
        </p>

        <RhInput
          data-testid="min-exp-skill-input"
          value={skillInfo.min_exp}
          key={JSON.stringify(skillInfo.min_exp)}
          onChange={(e) =>
            setSkillInfo((state) => ({ ...state, min_exp: e.target.value }))
          }
        />
      </div>

      <Button
        data-testid="add-skill-btn"
        variant={"secondary"}
        className="mt-4"
        type="button"
        onClick={() => {
          setSkillInfo({ skill: undefined, min_exp: undefined });
          const skillGroup = [...values.priority_skills__form_value];
          const nonDealBreakerSkills = skillGroup.splice(-1)[0] ?? [];

          if (skillGroup.length === 0) skillGroup.push([]);

          nonDealBreakerSkills.push(skillInfo);

          setFieldValue("priority_skills__form_value", [
            ...skillGroup,
            nonDealBreakerSkills,
          ]);
        }}
        disabled={!skillInfo.skill}
      >
        Add
      </Button>
    </div>
  );
};
